import ServiceEndpoints from '../ServiceEndpoints';

export interface CAEndUserAssignmentSummary {
    userEmail: string;
    ncaCode: string;
    assignedLegalBases: EUSectoralLegislatureBasis[];
}

export interface EUSectoralLegislatureBasis {
    id: number;
    name: string;
    active: boolean;
}

export interface CAEndUserAssignmentCreate {
    userEmail: string;
    euSectoralLegislatureBasisIds: number[];
    ncaCode: string;
}

export interface CAEndUserAssignmentUpdate {
    id: number;
    userEmail: string;
    euSectoralLegislatureBasisIds: number[];
    ncaCode: string;
}

// Fetch all CA end user assignments
export const fetchCAEndUserAssignments = async (token: string | undefined): Promise<CAEndUserAssignmentSummary[]> => {
    try {
        const response = await fetch(ServiceEndpoints.FetchCAEndUserAssignments, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching CA end user assignments:', error);
        throw error;
    }
};

// Fetch available CA end users
export const fetchAvailableCAEndUsers = async (token: string | undefined): Promise<string[]> => {
    try {
        const response = await fetch(ServiceEndpoints.FetchAvailableCAEndUsers, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching available CA end users:', error);
        throw error;
    }
};

// Fetch active legal bases
export const fetchActiveLegalBases = async (token: string | undefined): Promise<EUSectoralLegislatureBasis[]> => {
    try {
        const response = await fetch(ServiceEndpoints.FetchActiveLegalBases, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching active legal bases:', error);
        throw error;
    }
};

// Create new CA end user assignment
export const createCAEndUserAssignment = async (
    assignment: CAEndUserAssignmentCreate,
    token: string | undefined
): Promise<any> => {
    try {
        const response = await fetch(ServiceEndpoints.CreateCAEndUserAssignment, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(assignment),
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || `HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error creating CA end user assignment:', error);
        throw error;
    }
};

// Update CA end user assignment
export const updateCAEndUserAssignment = async (
    userEmail: string,
    assignment: CAEndUserAssignmentUpdate,
    token: string | undefined
): Promise<any> => {
    try {
        const response = await fetch(`${ServiceEndpoints.UpdateCAEndUserAssignment}/${encodeURIComponent(userEmail)}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(assignment),
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || `HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error updating CA end user assignment:', error);
        throw error;
    }
};

// Delete CA end user assignments
export const deleteCAEndUserAssignments = async (
    userEmail: string,
    token: string | undefined
): Promise<any> => {
    try {
        const response = await fetch(`${ServiceEndpoints.DeleteCAEndUserAssignment}/${encodeURIComponent(userEmail)}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || `HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error deleting CA end user assignments:', error);
        throw error;
    }
};

// Get users assigned to a specific legal basis
export const fetchUsersAssignedToLegalBasis = async (
    legalBasisId: number,
    token: string | undefined
): Promise<string[]> => {
    try {
        const response = await fetch(`${ServiceEndpoints.FetchUsersAssignedToLegalBasis}/${legalBasisId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching users assigned to legal basis:', error);
        throw error;
    }
};